import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../hooks/useApi';
import { AuthContext } from '../context/AuthContext';
import Modal from '../components/Modal';
import apiService from '../services/apiService';

const WasteRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useContext(AuthContext);
  const { data: request, isLoading, error } = useApi(`/waste/waste-requests/${id}`);
  const [currentStep, setCurrentStep] = useState(0); // 0: Details, 1: Bid Details, 2: Confirm Bid
  const [bidForm, setBidForm] = useState({ amount: '', note: '' });
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bidDetails, setBidDetails] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  useEffect(() => {
    if (currentStep >= 1 && !isAuthenticated()) {
      setIsLoginModalOpen(true);
    } else if (currentStep >= 1 && user?.role !== 'waste_collector' && !isRoleModalOpen) {
      setIsRoleModalOpen(true);
    }
  }, [currentStep, isAuthenticated, user, isRoleModalOpen]);

  const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : 'N/A');

  const handleStartBidding = () => {
    setCurrentStep(1);
  };

  const handleNext = () => {
    if (currentStep === 1 && (!bidForm.amount || bidForm.amount <= 0)) {
      toast.error('Please enter a valid bid amount');
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmitBid = async () => {
    setIsSubmitting(true);
    try {
      const response = await apiService.post(`/waste/bid/${id}`, {
        amount: parseFloat(bidForm.amount),
        note: bidForm.note,
      });
      setBidDetails({
        amount: bidForm.amount,
        note: bidForm.note || 'No note provided',
        requestType: request.type,
        farmerName: `${request.farmerId?.firstName} ${request.farmerId?.lastName}`,
        collectorName: `${user?.firstName} ${user?.lastName}`,
      });
      setIsConfirmationModalOpen(true);
    } catch (error) {
      toast.error(error.message || 'Failed to place bid');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoginClose = () => {
    setIsLoginModalOpen(false);
    setCurrentStep(0);
    navigate('/login');
  };

  const handleRoleClose = () => {
    setIsRoleModalOpen(false);
    setCurrentStep(0);
  };

  const handleRoleRedirect = () => {
    setIsRoleModalOpen(false);
    setCurrentStep(0);
    navigate('/signup');
  };

  const handleConfirmationClose = () => {
    setIsConfirmationModalOpen(false);
    navigate('/');
  };

  const handleImageClick = (attachment) => {
    if (attachment.match(/\.(jpg|jpeg|png|gif)$/i)) {
      setSelectedImage(`${BASE_URL}${attachment}`);
      setIsImageModalOpen(true);
    }
  };

  const handleImageModalClose = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  if (isLoading) {
    return (
      <div className="text-center text-gray-600 text-base sm:text-lg font-medium py-12">
        Loading waste request...
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="text-center text-red-600 text-base sm:text-lg font-medium py-12">
        Error loading waste request: {error?.message || 'Request not found'}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12 font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary text-center mb-4 sm:mb-6">
          Waste Request Details
        </h1>
        <div className="text-sm sm:text-base text-gray-500 text-center mb-6 sm:mb-8">
          Home  Waste Requests  Details
        </div>
        {currentStep > 0 && (
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 bg-white shadow-md rounded-lg p-3 sm:p-4 border border-gray-100">
              {[1, 2].map((step) => (
                <div key={step} className="flex items-center gap-2 sm:gap-3">
                  <div
                    className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full text-sm font-medium transition-all duration-300 ${
                      currentStep >= step
                        ? 'bg-gradient-to-r from-secondary to-opacity-90 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {step}
                  </div>
                  {step < 2 && <div className="hidden sm:block h-0.5 w-8 sm:w-12 bg-gray-200"></div>}
                  <span
                    className={`text-sm sm:text-base ${
                      currentStep >= step ? 'text-gray-900 font-semibold' : 'text-gray-500'
                    }`}
                  >
                    {['Bid Details', 'Confirmation'][step - 1]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 max-w-5xl mx-auto border border-gray-100">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Column (Desktop) or Main Content (Mobile) */}
            <div className="w-full lg:w-1/2 order-1">
              {/* Profile Section */}
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 mb-6 sm:mb-8">
                <img
                  src={
                    request.attachments?.[0]
                      ? `${BASE_URL}${request.attachments[0]}`
                      : 'https://via.placeholder.com/150'
                  }
                  alt={request.type}
                  className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-full border-4 border-secondary/20 cursor-pointer hover:opacity-90 transition duration-200"
                  onClick={() => handleImageClick(request.attachments?.[0] || '/placeholder.jpg')}
                />
                <div className="flex flex-col sm:flex-row sm:justify-between w-full text-center sm:text-left">
                  <div>
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary">
                      {request.type.charAt(0).toUpperCase() + request.type.slice(1)} Waste
                    </h2>
                    <p className="text-secondary text-sm sm:text-base font-medium">
                      Posted by: {request.farmerId?.firstName} {request.farmerId?.lastName}
                    </p>
                    <p className="text-gray-600 text-sm sm:text-base">
                      {request.location?.address}, {request.location?.city}, {request.location?.state},{' '}
                      {request.location?.country} {request.location?.postalCode}
                    </p>
                  </div>
                </div>
              </div>

              {/* Attachments (Mobile) */}
              <div className="lg:hidden order-2 mb-6 sm:mb-8">
                <div className="bg-gray-50 rounded-lg shadow-sm p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-primary mb-4">
                    Attachments
                  </h3>
                  {request.attachments?.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4">
                      {request.attachments.map((attachment, idx) => (
                        <div key={idx} className="flex flex-col items-center">
                          {attachment.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                            <img
                              src={`${BASE_URL}${attachment}`}
                              alt={`Attachment ${idx + 1}`}
                              className="h-40 object-cover rounded-lg cursor-pointer hover:opacity-90 transition duration-200"
                              onClick={() => handleImageClick(attachment)}
                            />
                          ) : (
                            <a
                              href={`${BASE_URL}${attachment}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block px-4 py-2 bg-secondary text-white rounded-lg hover:bg-opacity-90 text-sm sm:text-base font-medium transition duration-300"
                            >
                              Download Attachment {idx + 1}
                            </a>
                          )}
                          <p className="text-gray-600 text-sm mt-2">
                            Attachment {idx + 1}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 text-sm sm:text-base">
                      No attachments available.
                    </p>
                  )}
                </div>
              </div>

              {/* Details, Bids, and Place Bid Button (when currentStep === 0) */}
              {currentStep === 0 && (
                <div className="flex flex-col gap-6 sm:gap-8 order-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <p className="text-gray-700 text-sm font-semibold">Quantity</p>
                      <p className="text-gray-600 text-sm sm:text-base">{request.quantity} units</p>
                    </div>
                    <div>
                      <p className="text-gray-700 text-sm font-semibold">Status</p>
                      <p className="text-gray-600 text-sm sm:text-base">{request.status}</p>
                    </div>
                    <div>
                      <p className="text-gray-700 text-sm font-semibold">Priority</p>
                      <p className="text-gray-600 text-sm sm:text-base">{request.priority}</p>
                    </div>
                    <div>
                      <p className="text-gray-700 text-sm font-semibold">Estimated Value</p>
                      <p className="text-gray-600 text-sm sm:text-base">
                        ${request.estimatedValue.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-700 text-sm font-semibold">Deadline</p>
                      <p className="text-gray-600 text-sm sm:text-base">{formatDate(request.deadline)}</p>
                    </div>
                    <div>
                      <p className="text-gray-700 text-sm font-semibold">Processing Requirements</p>
                      <p className="text-gray-600 text-sm sm:text-base">
                        {request.processingRequirements || 'None'}
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="text-gray-700 text-sm font-semibold">Description</p>
                      <p className="text-gray-600 text-sm sm:text-base mt-1 leading-relaxed">
                        {request.description || 'No description provided.'}
                      </p>
                    </div>
                    {request.bids?.length > 0 && (
                      <div className="col-span-1 sm:col-span-2 lg:col-span-2">
                        <p className="text-gray-700 text-sm font-semibold mb-2">Bids</p>
                        <div className="space-y-3">
                          {request.bids.map((bid) => (
                            <div
                              key={bid._id}
                              className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition duration-200"
                            >
                              <p className="text-gray-700 text-sm sm:text-base">
                                <span className="font-semibold">Collector:</span> {bid.collectorId?.firstName} {bid.collectorId?.lastName}
                              </p>
                              <p className="text-gray-700 text-sm sm:text-base">
                                <span className="font-semibold">Amount:</span> ${bid.amount.toFixed(2)}
                              </p>
                              <p className="text-gray-700 text-sm sm:text-base">
                                <span className="font-semibold">Note:</span> {bid.note || 'No note'}
                              </p>
                              <p className="text-gray-700 text-sm sm:text-base">
                                <span className="font-semibold">Status:</span> {bid.status}
                              </p>
                              <p className="text-gray-700 text-sm sm:text-base">
                                <span className="font-semibold">Placed:</span> {formatDate(bid.createdAt)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleStartBidding}
                    className={`w-full px-6 py-3 rounded-lg text-base sm:text-lg font-semibold transition duration-300 ${
                      request.status === 'bidding'
                        ? 'bg-secondary text-white hover:bg-opacity-90'
                        : 'bg-gray-400 text-white cursor-not-allowed'
                    }`}
                    disabled={request.status !== 'bidding'}
                  >
                    {request.status === 'bidding' ? 'Place Bid' : 'Bidding Closed'}
                  </button>
                </div>
              )}

              {/* Bidding Form (when currentStep > 0) */}
              {currentStep > 0 && (
                <div className="mt-6 order-3">
                  {currentStep === 1 && (
                    <div className="p-4 sm:p-6 bg-gray-50 rounded-lg shadow-sm">
                      <h3 className="text-lg sm:text-xl font-semibold text-primary mb-4">
                        Bid Details
                      </h3>
                      <div className="space-y-5">
                        <div>
                          <label className="block text-gray-700 text-sm sm:text-base font-semibold mb-2">
                            Bid Amount ($)
                          </label>
                          <input
                            type="number"
                            value={bidForm.amount}
                            onChange={(e) => setBidForm({ ...bidForm, amount: e.target.value })}
                            placeholder="Enter bid amount"
                            min="0"
                            step="0.01"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary text-gray-700 text-sm sm:text-base bg-white"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-gray-700 text-sm sm:text-base font-semibold mb-2">
                            Note (Optional)
                          </label>
                          <textarea
                            value={bidForm.note}
                            onChange={(e) => setBidForm({ ...bidForm, note: e.target.value })}
                            placeholder="Enter any additional notes"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary h-32 text-gray-700 text-sm sm:text-base bg-white"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {currentStep === 2 && (
                    <div className="p-4 sm:p-6 bg-gray-50 rounded-lg shadow-sm">
                      <h3 className="text-lg sm:text-xl font-semibold text-primary mb-4">
                        Confirm Your Bid
                      </h3>
                      <div className="space-y-3 text-gray-700 text-sm sm:text-base">
                        <p>
                          <span className="font-semibold">Waste Type:</span>{' '}
                          {request.type.charAt(0).toUpperCase() + request.type.slice(1)}
                        </p>
                        <p>
                          <span className="font-semibold">Quantity:</span> {request.quantity} units
                        </p>
                        <p>
                          <span className="font-semibold">Farmer:</span>{' '}
                          {request.farmerId?.firstName} {request.farmerId?.lastName}
                        </p>
                        <p>
                          <span className="font-semibold">Bid Amount:</span> ${parseFloat(bidForm.amount).toFixed(2)}
                        </p>
                        <p>
                          <span className="font-semibold">Note:</span> {bidForm.note || 'No note provided'}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row justify-between gap-4 mt-6">
                    <button
                      onClick={handleBack}
                      className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition duration-300 text-base sm:text-lg font-semibold"
                      disabled={isSubmitting}
                    >
                      Back
                    </button>
                    <button
                      onClick={currentStep === 1 ? handleNext : handleSubmitBid}
                      className={`bg-secondary text-white px-6 py-3 rounded-lg hover:bg-opacity-90 transition duration-300 font-semibold text-base sm:text-lg ${
                        isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      disabled={isSubmitting}
                    >
                      {currentStep === 1 ? 'Review Bid' : isSubmitting ? 'Submitting...' : 'Confirm Bid'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Attachments (Desktop) */}
            <div className="hidden lg:block w-full lg:w-1/2 order-2">
              <div className="bg-gray-50 rounded-lg shadow-sm p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-primary mb-4">
                  Attachments
                </h3>
                {request.attachments?.length > 0 ? (
                  <div className="grid gap-4">
                    {request.attachments.map((attachment, idx) => (
                      <div key={idx} className="flex flex-col items-center">
                        {attachment.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                          <img
                            src={`${BASE_URL}${attachment}`}
                            alt={`Attachment ${idx + 1}`}
                            className="h-50 object-cover rounded-lg cursor-pointer hover:opacity-90 transition duration-200"
                            onClick={() => handleImageClick(attachment)}
                          />
                        ) : (
                          <a
                            href={`${BASE_URL}${attachment}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-4 py-2 bg-secondary text-white rounded-lg hover:bg-opacity-90 text-sm sm:text-base font-medium transition duration-300"
                          >
                            Download Attachment {idx + 1}
                          </a>
                        )}
                        <p className="text-gray-600 text-sm mt-2">
                          Attachment {idx + 1}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 text-sm sm:text-base">
                    No attachments available.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Login Modal */}
        <Modal
          isOpen={isLoginModalOpen}
          onClose={handleLoginClose}
          title="Login Required"
          className="bg-white rounded-xl shadow-lg p-4 sm:p-6 max-w-md mx-auto"
        >
          <p className="text-gray-700 text-sm sm:text-base">
            Please log in to place a bid on this waste request.
          </p>
          <button
            onClick={handleLoginClose}
            className="mt-4 w-full bg-secondary text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition duration-300 font-semibold text-sm sm:text-base"
          >
            Go to Login
          </button>
        </Modal>

        {/* Role Modal */}
        <Modal
          isOpen={isRoleModalOpen}
          onClose={handleRoleClose}
          title="Waste Collector Role Required"
          className="bg-white rounded-xl shadow-lg p-4 sm:p-6 max-w-md mx-auto"
        >
          <p className="text-gray-700 text-sm sm:text-base">
            Only waste collectors can place bids. Please sign up or update your profile to become a waste collector.
          </p>
          <div className="flex justify-between space-x-4 mt-4">
            <button
              onClick={handleRoleClose}
              className="flex-1 bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500 transition duration-300 font-semibold text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              onClick={handleRoleRedirect}
              className="flex-1 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition duration-300 font-semibold text-sm sm:text-base"
            >
              Update Profile
            </button>
          </div>
        </Modal>

        {/* Confirmation Modal */}
        <Modal
          isOpen={isConfirmationModalOpen}
          onClose={handleConfirmationClose}
          title="Bid Placed Successfully"
          className="bg-white rounded-xl shadow-lg p-4 sm:p-6 max-w-md mx-auto"
        >
          <p className="text-gray-700 text-sm sm:text-base mb-4">
            Thank you for placing your bid!
          </p>
          <div className="space-y-3 text-gray-700 text-sm sm:text-base">
            <p>
              <span className="font-semibold">Waste Type:</span> {bidDetails?.requestType}
            </p>
            <p>
              <span className="font-semibold">Farmer:</span> {bidDetails?.farmerName}
            </p>
            <p>
              <span className="font-semibold">Collector:</span> {bidDetails?.collectorName}
            </p>
            <p>
              <span className="font-semibold">Bid Amount:</span> ${parseFloat(bidDetails?.amount).toFixed(2)}
            </p>
            <p>
              <span className="font-semibold">Note:</span> {bidDetails?.note}
            </p>
          </div>
          <button
            onClick={handleConfirmationClose}
            className="mt-6 w-full bg-secondary text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition duration-300 font-semibold text-sm sm:text-base"
          >
            OK
          </button>
        </Modal>

        {/* Image Modal */}
        <Modal
          isOpen={isImageModalOpen}
          onClose={handleImageModalClose}
          title="View Image"
          className="bg-black bg-opacity-95 p-4 sm:p-6 max-w-5xl mx-auto rounded-xl"
        >
          <div className="flex justify-center items-center">
            <img
              src={selectedImage}
              alt="Full-size attachment"
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
          </div>
          <button
            onClick={handleImageModalClose}
            className="mt-4 w-full bg-secondary text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition duration-300 font-semibold text-sm sm:text-base"
          >
            Close
          </button>
        </Modal>
      </div>
    </div>
  );
};

export default WasteRequestDetails;